import { useCallback, useEffect, useRef, useState } from "react";
import "./ReportSheet.css";
import { REPORT_CONFIG } from "../data/spotterNetwork";

const SOURCES = [
  { key: "mping",   label: "mPING",         color: "#06b6d4" },
  { key: "spotter", label: "Chaser Network", color: "#f59e0b" },
];

const TYPE_ICONS = {
  tornado: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 5h14" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M7 9h10" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M9 13h6"  stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M11 17h2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  ),
  funnel: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M5 6h14" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M7 6c1 3 2 5 5 7s4 5 4 7"  stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M17 6c-1 3-2 5-5 7" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  hail: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 13a5 5 0 1 1 9.9-1H17a3 3 0 0 1 0 6H7a4 4 0 0 1-1-7.87" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="9"  cy="20" r="1" fill="currentColor" />
      <circle cx="15" cy="20" r="1" fill="currentColor" />
      <circle cx="12" cy="22" r="1" fill="currentColor" />
    </svg>
  ),
  wind: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M4 9h12a3 3 0 1 0-3-3"  stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M4 13h16"               stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M4 17h10a3 3 0 1 1-3 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  downburst: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M6 11a5 5 0 0 1 9.9-.5H17a3 3 0 0 1 0 6h-1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="8"  y1="16" x2="8"  y2="22" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M5.5 19.5l2.5 2.5 2.5-2.5"   stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <line x1="15" y1="16" x2="15" y2="22" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M12.5 19.5l2.5 2.5 2.5-2.5"  stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
  flood: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M2 7c1.5-2.5 3-2.5 4.5 0s3 2.5 4.5 0 3-2.5 4.5 0 3 2.5 4.5 0"  stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M2 13c1.5-2.5 3-2.5 4.5 0s3 2.5 4.5 0 3-2.5 4.5 0 3 2.5 4.5 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <path d="M2 19c1.5-2.5 3-2.5 4.5 0s3 2.5 4.5 0 3-2.5 4.5 0 3 2.5 4.5 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  ),
  rain: (
    <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <line x1="12" y1="3"   x2="12" y2="21"  stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="3"  y1="12"  x2="21" y2="12"  stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="5.6" y1="5.6" x2="18.4" y2="18.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <line x1="18.4" y1="5.6" x2="5.6" y2="18.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="12" cy="12" r="1.8" fill="currentColor" />
    </svg>
  ),
};

export default function ReportSheet({ isOpen, onClose, onSubmit }) {
  const [selectedType, setSelectedType] = useState(null);
  const [note,         setNote]         = useState("");
  const [submitted,    setSubmitted]    = useState(false);
  const [source,       setSource]       = useState("mping");
  const [reportDate,   setReportDate]   = useState("");
  const [reportTime,   setReportTime]   = useState("");
  const timerRef = useRef(null);

  // Reset form when sheet opens
  useEffect(() => {
    if (isOpen) {
      const now = new Date();
      setSelectedType(null);
      setNote("");
      setSubmitted(false);
      setSource("mping");
      setReportDate(now.toISOString().slice(0, 10));
      setReportTime(now.toISOString().slice(11, 16));
    }
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [isOpen]);

  const handleSubmit = useCallback(() => {
    if (!selectedType) return;
    onSubmit({ type: selectedType, note: note.trim(), source, ts: new Date(`${reportDate}T${reportTime}:00Z`).toISOString() });
    setSubmitted(true);
    timerRef.current = setTimeout(() => {
      setSubmitted(false);
      onClose();
    }, 1500);
  }, [selectedType, note, source, reportDate, reportTime, onSubmit, onClose]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, onClose]);

  return (
    <>
      <div
        className={`rs-backdrop${isOpen ? " rs-backdrop--open" : ""}`}
        onClick={onClose}
        aria-hidden="true"
      />
      <div
        className={`rs-sheet${isOpen ? " rs-sheet--open" : ""}`}
        role="dialog"
        aria-modal="true"
        aria-label="Submit a weather report"
      >
        <div className="rs-handle" aria-hidden="true" />

        {submitted ? (
          <div className="rs-submitted">
            <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" className="rs-submitted-icon">
              <circle cx="12" cy="12" r="10" stroke="#4ade80" strokeWidth="1.8" />
              <path d="M7 12.5l3.5 3.5 6.5-7" stroke="#4ade80" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="rs-submitted-text">Report submitted!</span>
          </div>
        ) : (
          <>
            <div className="rs-header">
              <h2 className="rs-title">Submit Report</h2>
              <button type="button" className="rs-close" onClick={onClose} aria-label="Close">
                <svg viewBox="0 0 12 12" fill="none" width="12" height="12" aria-hidden="true">
                  <line x1="1" y1="1" x2="11" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                  <line x1="11" y1="1" x2="1" y2="11" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
              </button>
            </div>

            <div className="rs-source-row">
              {SOURCES.map(({ key, label, color }) => (
                <button
                  key={key}
                  type="button"
                  className={`rs-source-btn${source === key ? " rs-source-btn--active" : ""}`}
                  style={source === key ? { "--src-color": color } : {}}
                  onClick={() => setSource(key)}
                >
                  <span className="rs-source-dot" style={{ background: color }} aria-hidden="true" />
                  {label}
                </button>
              ))}
            </div>

            <div className="rs-datetime-row">
              <div className="rs-field">
                <label className="rs-field-label" htmlFor="rs-date">Date (UTC)</label>
                <input
                  id="rs-date"
                  type="date"
                  className="rs-datetime-input"
                  value={reportDate}
                  onChange={(e) => setReportDate(e.target.value)}
                />
              </div>
              <div className="rs-field">
                <label className="rs-field-label" htmlFor="rs-time">Time (UTC)</label>
                <input
                  id="rs-time"
                  type="time"
                  className="rs-datetime-input"
                  value={reportTime}
                  onChange={(e) => setReportTime(e.target.value)}
                />
              </div>
            </div>

            <div className="rs-section-label">Report type</div>
            <div className="rs-type-grid">
              {Object.entries(REPORT_CONFIG).map(([key, cfg]) => {
                const active = selectedType === key;
                return (
                  <button
                    key={key}
                    type="button"
                    className={`rs-type-btn${active ? " rs-type-btn--active" : ""}`}
                    style={active ? { "--type-color": cfg.color } : {}}
                    onClick={() => setSelectedType(active ? null : key)}
                    aria-pressed={active}
                  >
                    <span className="rs-type-icon" style={{ color: active ? cfg.color : undefined }}>
                      {TYPE_ICONS[key]}
                    </span>
                    <span className="rs-type-label">{cfg.label}</span>
                  </button>
                );
              })}
            </div>

            <div className="rs-section-label">Note <span className="rs-optional">(optional)</span></div>
            <textarea
              className="rs-note"
              rows={3}
              maxLength={280}
              placeholder="Brief description…"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />

            <button
              type="button"
              className="rs-submit"
              disabled={!selectedType}
              onClick={handleSubmit}
            >
              Submit Report
            </button>
          </>
        )}
      </div>
    </>
  );
}
